import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, X, Keyboard, Loader2, AlertTriangle, RefreshCw, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

/**
 * BarcodeScanner — scanner QR + barcode (EAN-13, EAN-8, Code128, Code39, UPC, ITF) a schermo intero.
 * props:
 *   open: bool
 *   onClose: () => void
 *   onDetected: (codice: string) => void
 *
 * Note di implementazione (fix anteprima nera su Android):
 * - Il <video> è nostro (non iniettato da html5-qrcode) e lo stream arriva da getUserMedia diretto.
 * - Il contenitore è a schermo intero SENZA transform / animazioni / overflow-scroll: dentro il
 *   DialogContent shadcn (translate + zoom + overflow-y-auto) Chrome Android può comporre il video
 *   della fotocamera come rettangolo nero pur con la fotocamera attiva.
 * - Decodifica con BarcodeDetector nativo (Chrome Android); fallback al decoder di html5-qrcode
 *   su frame statici (iOS / browser senza BarcodeDetector).
 * - Watchdog: se l'anteprima resta nera (videoWidth 0 o frame tutti neri) passa automaticamente
 *   alla fotocamera posteriore successiva (alcuni Android espongono sensori ausiliari/depth).
 */

const NATIVE_FORMATS = ["qr_code", "ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "itf"];
const H5_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
];
const DECODE_HOST_ID = "bc-decode-host";
const BACK_RE = /back|rear|environment|posteriore|trasera|arrière|hinten/i;
const BLACK_TIMEOUT_MS = 3000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const stopStream = (s) => { try { s?.getTracks().forEach((t) => t.stop()); } catch {} };

const errorMessage = (e) => {
  const name = e?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    return "Permesso fotocamera negato. Chrome Android: tocca il lucchetto accanto all'URL → Autorizzazioni → Fotocamera → Consenti. iPhone: Impostazioni → Safari → Fotocamera → Consenti. Poi tocca Riprova.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
    return "Nessuna fotocamera utilizzabile rilevata su questo dispositivo.";
  }
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
    return "Fotocamera occupata da un'altra app (Fotocamera, WhatsApp, Meet...). Chiudila dallo switcher app e tocca Riprova.";
  }
  if (name === "InsecureContext") return "La fotocamera richiede una connessione HTTPS.";
  return `Fotocamera non disponibile${e?.message ? `: ${e.message}` : ""}`;
};

// Ordina le fotocamere: quella attuale esclusa, posteriori prima (la "0" di solito è la principale).
const orderCameras = (cams) => {
  const score = (c) => {
    const l = c.label || "";
    let s = 0;
    if (BACK_RE.test(l)) s -= 10;
    if (/\b0\b/.test(l)) s -= 1;
    return s;
  };
  return [...cams].sort((a, b) => score(a) - score(b));
};

async function createNativeDetector() {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
  try {
    const supported = await window.BarcodeDetector.getSupportedFormats();
    const formats = NATIVE_FORMATS.filter((f) => supported.includes(f));
    if (!formats.length) return null;
    return new window.BarcodeDetector({ formats });
  } catch {
    return null;
  }
}

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const sessionRef = useRef(0);       // incrementato ad ogni stop: invalida start/loop in corso
  const doneRef = useRef(false);      // codice già consegnato in questa apertura
  const camsRef = useRef([]);         // fotocamere video ordinate
  const camIdxRef = useRef(-1);       // indice della fotocamera attuale in camsRef (-1 = facingMode)
  const autoTriedRef = useRef(0);     // quante fotocamere ha già provato il watchdog
  const canvasRef = useRef(null);
  const h5Ref = useRef(null);

  const [phase, setPhase] = useState("idle"); // idle | starting | live | error
  const [errorMsg, setErrorMsg] = useState("");
  const [blackHint, setBlackHint] = useState(false);
  const [camCount, setCamCount] = useState(0);
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState("");

  const stopCamera = () => {
    sessionRef.current++;
    stopStream(streamRef.current);
    streamRef.current = null;
    const v = videoRef.current;
    if (v) {
      try { v.pause(); } catch {}
      v.srcObject = null;
    }
  };

  const deliver = (code) => {
    if (doneRef.current || !code) return;
    doneRef.current = true;
    stopCamera();
    try { navigator.vibrate?.(60); } catch {}
    onDetected?.(code);
  };

  const getCanvas = () => {
    if (!canvasRef.current) canvasRef.current = document.createElement("canvas");
    return canvasRef.current;
  };

  // true se il frame attuale è (quasi) completamente nero
  const frameIsBlack = (v) => {
    if (!v.videoWidth || !v.videoHeight) return true;
    try {
      const c = getCanvas();
      c.width = 32; c.height = 32;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, 32, 32);
      const d = ctx.getImageData(0, 0, 32, 32).data;
      let max = 0;
      for (let i = 0; i < d.length; i += 4) {
        const y = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
        if (y > max) max = y;
      }
      return max < 12;
    } catch {
      return false; // canvas non leggibile: non possiamo giudicare, assumiamo ok
    }
  };

  const decodeWithHtml5 = async (v) => {
    if (!h5Ref.current) {
      if (!document.getElementById(DECODE_HOST_ID)) return null;
      h5Ref.current = new Html5Qrcode(DECODE_HOST_ID, { formatsToSupport: H5_FORMATS, verbose: false });
    }
    const c = getCanvas();
    const scale = Math.min(1, 800 / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    if (!blob) return null;
    try {
      return await h5Ref.current.scanFile(new File([blob], "frame.jpg", { type: "image/jpeg" }), false);
    } catch {
      return null; // nessun codice in questo frame
    }
  };

  // Loop di decodifica + watchdog anteprima nera, legato alla sessione corrente
  const runLoop = async (session) => {
    const detector = await createNativeDetector();
    const interval = detector ? 150 : 450;
    let blackSince = Date.now();
    while (session === sessionRef.current && !doneRef.current) {
      const v = videoRef.current;
      if (v && v.readyState >= 2) {
        if (frameIsBlack(v)) {
          if (Date.now() - blackSince > BLACK_TIMEOUT_MS) {
            const total = camsRef.current.length;
            if (autoTriedRef.current < total - 1) {
              autoTriedRef.current++;
              switchCamera(true);
              return;
            }
            setBlackHint(true);
          }
        } else {
          blackSince = Date.now();
          setBlackHint(false);
          try {
            let code = null;
            if (detector) {
              const found = await detector.detect(v);
              code = found?.[0]?.rawValue || null;
            } else {
              code = await decodeWithHtml5(v);
            }
            if (code && session === sessionRef.current) { deliver(code); return; }
          } catch {}
        }
      }
      await sleep(interval);
    }
  };

  // DEVE poter essere chiamata direttamente nel click handler: getUserMedia è il primo await.
  const startCamera = async (deviceId) => {
    stopCamera();
    const session = sessionRef.current;
    setErrorMsg("");
    setBlackHint(false);
    setPhase("starting");
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw Object.assign(new Error(""), { name: window.isSecureContext ? "NotSupported" : "InsecureContext" });
      }
      const video = deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
        : { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
      } catch (e) {
        if (e?.name === "NotAllowedError" || e?.name === "SecurityError") throw e;
        // vincoli non soddisfatti / camera occupata: un ritentativo con vincoli minimi
        await sleep(500);
        if (session !== sessionRef.current) return;
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      if (session !== sessionRef.current) { stopStream(stream); return; }
      streamRef.current = stream;

      const v = videoRef.current;
      if (!v) { stopCamera(); return; }
      v.muted = true;
      v.setAttribute("playsinline", "");
      v.setAttribute("muted", "");
      v.srcObject = stream;
      try { await v.play(); } catch (e) { if (e?.name !== "AbortError") throw e; }
      if (session !== sessionRef.current) return;

      // Ora le etichette sono disponibili: memorizza le fotocamere e quale stiamo usando
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = orderCameras(devs.filter((d) => d.kind === "videoinput"));
        camsRef.current = cams;
        setCamCount(cams.length);
        const activeId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
        camIdxRef.current = cams.findIndex((c) => c.deviceId === activeId);
      } catch {}

      setPhase("live");
      runLoop(session);
    } catch (e) {
      if (session !== sessionRef.current) return;
      stopCamera();
      setErrorMsg(errorMessage(e));
      setPhase("error");
      try { console.warn("[BarcodeScanner]", e?.name, e?.message); } catch {}
    }
  };

  const switchCamera = (auto = false) => {
    const cams = camsRef.current;
    if (cams.length < 2) return;
    if (!auto) autoTriedRef.current = cams.length; // scelta manuale: niente più switch automatici
    const next = (camIdxRef.current + 1 + cams.length) % cams.length;
    camIdxRef.current = next;
    startCamera(cams[next].deviceId);
  };

  // Apertura/chiusura: se il permesso è già concesso parte da solo, altrimenti serve il tap
  // (iOS Safari concede getUserMedia solo dentro un gesto utente).
  useEffect(() => {
    if (!open) { stopCamera(); return; }
    doneRef.current = false;
    autoTriedRef.current = 0;
    h5Ref.current = null; // l'host del decoder viene ri-montato ad ogni apertura
    setManualMode(false);
    setManual("");
    setErrorMsg("");
    setBlackHint(false);
    setPhase("idle");
    let cancelled = false;
    (async () => {
      try {
        const p = await navigator.permissions?.query({ name: "camera" });
        if (!cancelled && p?.state === "granted") startCamera();
      } catch {}
    })();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Rilascia la fotocamera se l'app va in background (PWA) e la riprende al ritorno
  useEffect(() => {
    if (!open) return;
    const onVis = () => {
      if (document.hidden) {
        if (streamRef.current) { stopCamera(); setPhase("idle"); }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [open]);

  useEffect(() => () => {
    stopCamera();
    try { h5Ref.current?.clear(); } catch {}
    h5Ref.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestClose = () => {
    stopCamera();
    onClose?.();
  };

  const submitManual = () => {
    const v = (manual || "").trim();
    if (!v) return toast.error("Inserisci un codice");
    doneRef.current = true;
    stopCamera();
    onDetected?.(v);
    setManual("");
  };

  const toggleManual = () => {
    if (!manualMode) {
      stopCamera();
      setPhase("idle");
      setManualMode(true);
    } else {
      setManualMode(false);
      startCamera();
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[100] flex flex-col bg-black text-white outline-none"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-background text-foreground border-b">
            <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-semibold">
              <ScanLine className="h-5 w-5 text-primary" /> Scanner Barcode / QR
            </DialogPrimitive.Title>
            <button
              type="button"
              aria-label="Chiudi"
              onClick={requestClose}
              className="rounded-sm p-1 opacity-70 hover:opacity-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* host nascosto per il decoder di fallback html5-qrcode (scanFile) */}
          <div id={DECODE_HOST_ID} style={{ display: "none" }} />

          {!manualMode ? (
            <div className="relative flex-1 min-h-0 bg-black">
              <video
                ref={videoRef}
                data-testid="bc-video"
                autoPlay
                muted
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />

              {phase === "live" && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-[min(70vw,60vh)] w-[min(70vw,60vh)] rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
                </div>
              )}

              {phase === "idle" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <ScanLine className="h-10 w-10 text-primary" />
                  <div className="text-sm leading-snug">
                    Tocca il pulsante qui sotto per attivare la fotocamera.
                    <br />
                    <span className="text-xs text-white/60">(Il browser potrebbe chiedere il permesso.)</span>
                  </div>
                  <Button data-testid="bc-launch-btn" size="lg" className="h-12 px-6 text-base" onClick={() => startCamera()}>
                    <ScanLine className="mr-2 h-5 w-5" /> Avvia fotocamera
                  </Button>
                </div>
              )}

              {phase === "starting" && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Avvio fotocamera…
                </div>
              )}

              {phase === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 px-6 text-center">
                  <AlertTriangle className="h-8 w-8 text-amber-400" />
                  <div className="text-sm" data-testid="bc-error-msg">{errorMsg}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="bg-white text-black" onClick={() => startCamera()} data-testid="bc-retry">
                      <RefreshCw className="mr-1 h-4 w-4" /> Riprova
                    </Button>
                    <Button size="sm" onClick={toggleManual}>
                      <Keyboard className="mr-1 h-4 w-4" /> Inserisci a mano
                    </Button>
                  </div>
                </div>
              )}

              {phase === "live" && (
                <div className="pointer-events-none absolute bottom-3 left-0 right-0 px-4 text-center text-xs text-white/90">
                  {blackHint
                    ? "L'anteprima è nera: tocca \"Cambia fotocamera\" o verifica che l'obiettivo non sia coperto."
                    : "Inquadra il codice QR o barcode"}
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 space-y-3 bg-background p-4 text-foreground">
              <div className="text-sm text-muted-foreground">Inserisci manualmente il codice (es. ATT-001):</div>
              <Input
                data-testid="bc-manual-input"
                autoFocus
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitManual()}
                placeholder="Codice QR / Barcode"
              />
              <Button data-testid="bc-manual-ok" onClick={submitManual} className="w-full">
                Conferma codice
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t bg-background p-3 text-foreground">
            <Button data-testid="bc-toggle-manual" variant="outline" size="sm" onClick={toggleManual}>
              <Keyboard className="mr-1 h-4 w-4" />
              {manualMode ? "Usa fotocamera" : "Inserisci manualmente"}
            </Button>
            {!manualMode && phase === "live" && camCount > 1 && (
              <Button data-testid="bc-switch-cam" variant="outline" size="sm" onClick={() => switchCamera(false)}>
                <SwitchCamera className="mr-1 h-4 w-4" /> Cambia fotocamera
              </Button>
            )}
            <Button data-testid="bc-close" variant="ghost" size="sm" onClick={requestClose}>
              <X className="mr-1 h-4 w-4" /> Chiudi
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
